import { gatewayCredentialScope } from "@openclaw/gateway-client/browser";
import { isRecord } from "@openclaw/normalization-core/record-coerce";
import {
  DEVICE_AUTH_STORAGE_KEY_PREFIX,
  LEGACY_DEVICE_AUTH_STORAGE_KEY,
} from "../../../../src/shared/control-ui-storage.js";
import {
  type DeviceAuthEntry,
  type DeviceAuthStore,
  normalizeDeviceAuthRole,
  normalizeDeviceAuthScopes,
} from "../../../../src/shared/device-auth.js";
import { getSafeLocalStorage } from "../../local-storage.ts";
import { base64ToBytes, bytesToBase64 } from "../bytes-base64.ts";
import { bytesToHex } from "../bytes-hex.ts";

export type {
  DeviceTokenSummary,
  PairedDevice,
  PendingDevice,
} from "../../../../src/gateway/device-pairing-list.types.js";

// Plain-HTTP origins lack crypto.subtle; load the pure-JS digest only when
// fingerprinting an identity there. Secure contexts use the platform digest.
const loadPureSha2 = () => import("@noble/hashes/sha2.js");

type StoredIdentity = DeviceIdentity & {
  version: 1;
  createdAtMs: number;
};

type DeviceIdentity = {
  deviceId: string;
  publicKey: string;
  privateKey: string;
};

const DEVICE_IDENTITY_STORAGE_KEY = "openclaw-device-identity-v1";

function deviceAuthStorageKey(gatewayUrl: string): string {
  return `${DEVICE_AUTH_STORAGE_KEY_PREFIX}${gatewayCredentialScope(gatewayUrl)}`;
}

function removeLegacyDeviceAuthStore(storage: Storage | null) {
  try {
    storage?.removeItem(LEGACY_DEVICE_AUTH_STORAGE_KEY);
  } catch {
    // Legacy cleanup must not make an otherwise usable device token unreadable.
  }
}

function parseDeviceAuthStore(raw: string | null): DeviceAuthStore | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as DeviceAuthStore;
    if (!parsed || parsed.version !== 1) {
      return null;
    }
    if (!parsed.deviceId || typeof parsed.deviceId !== "string") {
      return null;
    }
    if (!parsed.tokens || typeof parsed.tokens !== "object") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function readStore(gatewayUrl: string): DeviceAuthStore | null {
  try {
    const storage = getSafeLocalStorage();
    const scopedKey = deviceAuthStorageKey(gatewayUrl);
    const scopedStore = parseDeviceAuthStore(storage?.getItem(scopedKey) ?? null);
    if (scopedStore) {
      removeLegacyDeviceAuthStore(storage);
      return scopedStore;
    }

    const legacyStore = parseDeviceAuthStore(
      storage?.getItem(LEGACY_DEVICE_AUTH_STORAGE_KEY) ?? null,
    );
    if (!legacyStore) {
      return null;
    }

    // Older releases stored one origin-wide token. Claim it for the first gateway
    // opened after upgrade, then remove the ambiguous key before sibling routes use it.
    try {
      storage?.setItem(scopedKey, JSON.stringify(legacyStore));
      removeLegacyDeviceAuthStore(storage);
    } catch {
      // Keep the usable in-memory result when browser storage rejects the migration.
    }
    return legacyStore;
  } catch {
    return null;
  }
}

function writeStore(gatewayUrl: string, store: DeviceAuthStore) {
  try {
    const storage = getSafeLocalStorage();
    storage?.setItem(deviceAuthStorageKey(gatewayUrl), JSON.stringify(store));
    removeLegacyDeviceAuthStore(storage);
  } catch {
    // localStorage can be unavailable in private or embedded contexts.
  }
}

function canonicalDeviceAuthTokens(tokens: DeviceAuthStore["tokens"]) {
  const canonical: DeviceAuthStore["tokens"] = {};
  for (const [rawRole, entry] of Object.entries(tokens)) {
    const role = normalizeDeviceAuthRole(rawRole);
    if (!role || !entry || typeof entry.token !== "string") {
      continue;
    }
    canonical[role] = {
      token: entry.token,
      role,
      scopes: normalizeDeviceAuthScopes(Array.isArray(entry.scopes) ? entry.scopes : undefined),
      updatedAtMs: Number.isFinite(entry.updatedAtMs) ? entry.updatedAtMs : 0,
    };
  }
  return canonical;
}

export function loadDeviceAuthToken(params: {
  deviceId: string;
  gatewayUrl: string;
  role: string;
}): DeviceAuthEntry | null {
  const store = readStore(params.gatewayUrl);
  if (!store || store.deviceId !== params.deviceId) {
    return null;
  }
  const role = normalizeDeviceAuthRole(params.role);
  return canonicalDeviceAuthTokens(store.tokens)[role] ?? null;
}

export function loadCurrentDeviceAuthToken(gatewayUrl: string): string | null {
  const identity = readStoredDeviceIdentity(getSafeLocalStorage());
  if (!identity) {
    return null;
  }
  const entry = loadDeviceAuthToken({
    deviceId: identity.deviceId,
    gatewayUrl,
    role: "operator",
  });
  return entry?.scopes.includes("operator.read") ? entry.token : null;
}

export function storeDeviceAuthToken(params: {
  deviceId: string;
  gatewayUrl: string;
  role: string;
  token: string;
  scopes?: string[];
}): DeviceAuthEntry {
  const existing = readStore(params.gatewayUrl);
  const role = normalizeDeviceAuthRole(params.role);
  const entry: DeviceAuthEntry = {
    token: params.token,
    role,
    scopes: normalizeDeviceAuthScopes(params.scopes),
    updatedAtMs: Date.now(),
  };
  writeStore(params.gatewayUrl, {
    version: 1,
    deviceId: params.deviceId,
    tokens: {
      ...(existing?.deviceId === params.deviceId ? canonicalDeviceAuthTokens(existing.tokens) : {}),
      [role]: entry,
    },
  });
  return entry;
}

export function clearDeviceAuthToken(params: {
  deviceId: string;
  gatewayUrl: string;
  role: string;
}) {
  const store = readStore(params.gatewayUrl);
  if (!store || store.deviceId !== params.deviceId) {
    return;
  }
  const role = normalizeDeviceAuthRole(params.role);
  // Canonicalize before the presence check: loadDeviceAuthToken reads
  // alias-keyed entries (e.g. " operator "), so an alias-only credential must
  // also be clearable — a raw-key check here would leave it readable forever.
  const tokens = canonicalDeviceAuthTokens(store.tokens);
  if (!tokens[role]) {
    return;
  }
  delete tokens[role];
  writeStore(params.gatewayUrl, { ...store, tokens });
}

function base64UrlEncode(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/g, "");
}

function base64UrlDecode(input: string): Uint8Array {
  const normalized = input.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  return base64ToBytes(padded);
}

async function fingerprintPublicKey(publicKey: Uint8Array): Promise<string> {
  // Prefer the platform digest where the context provides it; the pure-JS
  // fallback keeps identity working on plain-HTTP origins without subtle.
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const hash = await subtle.digest("SHA-256", publicKey.slice().buffer);
    return bytesToHex(new Uint8Array(hash));
  }
  return bytesToHex((await loadPureSha2()).sha256(publicKey));
}

async function generateIdentity(): Promise<DeviceIdentity> {
  // Ed25519 uses a 32-byte random seed. An unavailable RNG must fail before
  // loading the signer so the Gateway's device-less fallback stays immediate.
  const privateKey = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const { getPublicKeyAsync } = await import("./device-signing.runtime.ts");
  const publicKey = await getPublicKeyAsync(privateKey);
  const deviceId = await fingerprintPublicKey(publicKey);
  return {
    deviceId,
    publicKey: base64UrlEncode(publicKey),
    privateKey: base64UrlEncode(privateKey),
  };
}

/**
 * Synchronous identity probe for render gating: reads the stored device id
 * without creating, repairing, or fingerprint-verifying an identity, so a
 * "do we hold credentials?" check stays side-effect free before connect().
 */
export function peekStoredDeviceIdentityId(): string | null {
  try {
    const raw = getSafeLocalStorage()?.getItem(DEVICE_IDENTITY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) &&
      parsed.version === 1 &&
      typeof parsed.deviceId === "string" &&
      parsed.deviceId
      ? parsed.deviceId
      : null;
  } catch {
    return null;
  }
}

// Storage-blocked pages (for example private browsing) must still present one
// stable device per page lifetime; minting a fresh key on every reconnect
// would raise a new unpaired request each time and never retain approval.
let sessionDeviceIdentity: DeviceIdentity | null = null;

function readStoredDeviceIdentity(storage: Storage | null): StoredIdentity | null {
  try {
    const raw = storage?.getItem(DEVICE_IDENTITY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredIdentity;
      if (
        parsed?.version === 1 &&
        typeof parsed.deviceId === "string" &&
        typeof parsed.publicKey === "string" &&
        typeof parsed.privateKey === "string"
      ) {
        return parsed;
      }
    }
  } catch {
    // Unavailable or malformed browser storage carries no usable identity.
  }
  return null;
}

export async function loadOrCreateDeviceIdentity(): Promise<DeviceIdentity> {
  const storage = getSafeLocalStorage();
  const parsed = readStoredDeviceIdentity(storage);
  try {
    if (parsed) {
      const derivedId = await fingerprintPublicKey(base64UrlDecode(parsed.publicKey));
      if (derivedId !== parsed.deviceId) {
        const updated: StoredIdentity = {
          ...parsed,
          deviceId: derivedId,
        };
        storage?.setItem(DEVICE_IDENTITY_STORAGE_KEY, JSON.stringify(updated));
      }
      return {
        deviceId: derivedId,
        publicKey: parsed.publicKey,
        privateKey: parsed.privateKey,
      };
    }
  } catch {
    // Invalid local identity is replaced below.
  }

  if (sessionDeviceIdentity) {
    return sessionDeviceIdentity;
  }
  const identity = await generateIdentity();
  const stored: StoredIdentity = {
    version: 1,
    ...identity,
    createdAtMs: Date.now(),
  };
  try {
    storage?.setItem(DEVICE_IDENTITY_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // A write-rejecting store still gets the in-memory identity below.
  }
  sessionDeviceIdentity = identity;
  return identity;
}

export async function signDevicePayload(privateKeyBase64Url: string, payload: string) {
  const { signAsync } = await import("./device-signing.runtime.ts");
  const key = base64UrlDecode(privateKeyBase64Url);
  const data = new TextEncoder().encode(payload);
  const sig = await signAsync(data, key);
  return base64UrlEncode(sig);
}
